import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent, { timelineContentClasses } from '@mui/lab/TimelineContent';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import { Avatar, Button, Stack, Typography } from '@mui/material';
import moment from 'moment';
import { useStoreState } from 'pullstate';
import { useCallback, useEffect, useState } from 'react';
import { AppStore } from '../stores/AppStore.js';
import RichtextFormatter from './RichtextFormatter.jsx';
import SlateRenderer from './SlateRenderer.jsx';

export default function CommentsTimelineGoalForm(props) {
    const {
        setComment, comment, handleCommentsChange, title, goalSelectedToComment,
        goalDisabled, checkInGoalComments, setCheckInGoalComments, goalComments,
        setGoalComments, goalId, showCheckinComments = true
    } = props
    const [comments, setComments] = useState([])
    const [isAddingComment, setIsAddingComment] = useState(false)
    const checkIns = useStoreState(AppStore, s => s.checkIns)
    const goals = useStoreState(AppStore, s => s.goals)
    const checkInGoals = useStoreState(AppStore, s => s.checkInGoals)
    const [textfieldHelpText, setTextfieldHelpText] = useState('')
    const [errorTextfield, setErrorTextfield] = useState(false)
    const [loggedUser] = useState(useStoreState(AppStore, s => s.user))

    useEffect(() => {
        // When specific comments are passed via props (like from CheckinGoals), use only those
        if (props.comments && props.comments.length >= 0) {
            setComments(props.comments)
            return // Exit early to prevent mixing with other comment sources
        }

        // Only search for comments automatically if no comments are passed via props
        if (showCheckinComments && checkInGoals?.length > 0) {
            getCheckGoalComments();
        }
    }, [checkInGoals, props.comments, showCheckinComments]);

    useEffect(() => {
        // When specific comments are passed via props, don't search for goal comments
        if (props.comments && props.comments.length >= 0) {
            return // Exit early to prevent mixing with goal comments
        }

        // Only search for comments automatically if no comments are passed via props
        if (goals?.length > 0) {
            getGoalComments();
        }
    }, [goals, props.comments]);

    useEffect(() => {
        // When specific comments are passed via props, use only those
        if (props.comments && props.comments.length >= 0) {
            setComments(props.comments)
            return // Exit early to prevent joining with other comment sources
        }

        // Only join comments if no specific comments are passed via props
        if (showCheckinComments && (checkInGoalComments?.length > 0 || goalComments?.length > 0)) {
            joinCheckInGoalAndGoalComments();
        } else if (!showCheckinComments && goalComments?.length > 0) {
            // If not showing checkin comments, only use goal comments
            setComments(goalComments.sort((a, b) => new Date(a.commentDate) - new Date(b.commentDate)));
        }
    }, [checkInGoalComments, goalComments, showCheckinComments, props.comments])

    const getCheckGoalComments = useCallback(() => {

        if (!checkIns || !checkInGoals) {
            return
        }
        const validCheckInsId = checkIns?.filter(checkIn => checkIn.status !== 'archived').map(checkIn => checkIn.id)
        const checkInGoalCommentsObject = checkInGoals?.filter(goal => goal.goal_id === parseInt(goalId) && validCheckInsId.includes(goal.check_in_id) && goal.goal_comments).map(goal => goal.goal_comments && JSON.parse(goal.goal_comments))
        setCheckInGoalComments(checkInGoalCommentsObject?.flat() || [])
    }, [checkIns, checkInGoals, goalId, setCheckInGoalComments])

    const getGoalComments = useCallback(() => {
        if (!goals) {
            return
        }
        const goalComments = goals?.filter(goal => goal.id === parseInt(goalId)).map(goal => goal?.comments && JSON.parse(goal?.comments)).flat()
        setGoalComments(goalComments?.flat() || [])
    }, [goals, goalId, setGoalComments])

    const joinCheckInGoalAndGoalComments = useCallback(() => {
        const combinedComments = [...(checkInGoalComments || []), ...(goalComments || [])]
        const sortedComments = combinedComments.sort((a, b) => new Date(a.commentDate) - new Date(b.commentDate))
        setComments(sortedComments);
    }, [checkInGoalComments, goalComments])

    // Clear comments when goalId changes to prevent contamination between goals
    useEffect(() => {
        setComments([])

        if (props.comments && props.comments.length >= 0) {
            // Use only comments passed via props (from CheckinGoals)
            setComments(props.comments)
        } else {
            // Only clear and search if there are no comments via props (from GoalForm)
            setCheckInGoalComments([])
            setGoalComments([])

            if (goalId) {
                if (showCheckinComments && checkInGoals?.length > 0) {
                    getCheckGoalComments();
                }
                if (goals?.length > 0) {
                    getGoalComments();
                }
            }
        }
    }, [goalId, props.comments])

    // Handle props.comments changes from parent component
    useEffect(() => {
        if (isAddingComment) {
            return;
        }

        if (props.comments && props.comments.length >= 0) {
            setComments(props.comments)
        }
    }, [props.comments, isAddingComment])

    // Combine store comments only when props.comments is not provided
    useEffect(() => {
        if (isAddingComment) return;

        // Don't combine comments if specific comments are passed via props
        if (props.comments && props.comments.length >= 0) {
            return // Exit early to prevent mixing
        }

        if (showCheckinComments) {
            joinCheckInGoalAndGoalComments();
        } else if (goalComments?.length > 0) {
            // If not showing checkin comments, only use goal comments
            setComments(goalComments.sort((a, b) => new Date(a.commentDate) - new Date(b.commentDate)));
        }
    }, [checkInGoalComments, goalComments, props.comments, joinCheckInGoalAndGoalComments, isAddingComment, showCheckinComments])

    const handleAddComment = useCallback(() => {
        setIsAddingComment(true)

        const newComment = {
            comment: comment,
            commentDate: moment(),
            name: loggedUser.full_name,
            avatar: loggedUser.photo_url
        }

        // Always use current comments (which are from checkin) as base
        const updatedComments = [...comments, newComment]

        // Update locally first
        setComments(updatedComments)

        // Notify parent component immediately
        if (handleCommentsChange) {
            const targetGoalId = goalSelectedToComment?.id || goalId
            handleCommentsChange(targetGoalId, updatedComments)
        }

        setComment('')

        setTimeout(() => {
            setIsAddingComment(false);
        }, 100)
    }, [comments, comment, loggedUser, goalSelectedToComment, goalId, handleCommentsChange, setComment])

    const handleClickAddComment = () => {
        if (comment === '') {
            setTextfieldHelpText(`Comment should not be empty`)
            setErrorTextfield(true)
            return
        }
        handleAddComment()
    }

    return (
        <>
            <Timeline
                style={{ marginLeft: '0px', marginRight: '0px', padding: '0px' }}
                position='left'
                sx={{
                    [`& .${timelineContentClasses.root}`]: {
                        flex: 0.2,
                    },
                }}
            >
                {comments?.map((item, index) => (

                    <TimelineItem key={index} sx={{ width: '100%' }} >
                        <TimelineOppositeContent
                            sx={{
                                m: 'auto 0',
                                overflowWrap: 'anywhere',
                                whiteSpace: 'pre-wrap'
                            }}
                            align="left"
                            variant="body2"
                            color="text.secondary"
                        >
                            <SlateRenderer content={item.comment} disabled={goalDisabled} />
                        </TimelineOppositeContent>
                        <TimelineSeparator>
                            <TimelineConnector />
                            <Avatar src={item?.avatar} />
                            <TimelineConnector />
                        </TimelineSeparator>
                        <Stack sx={{ width: '20%', justifyContent: 'center' }}>
                            <TimelineContent style={{ paddingLeft: 0 }} sx={{ py: '12px', px: 2, textAlign: 'center' }}>
                                <Typography>{item?.name}</Typography>
                                <Typography style={{ paddingLeft: 0 }} sx={{ fontSize: '11px' }} color={'text.secondary'} >{moment(item?.commentDate).format('MMM DD, YYYY [at] h:mm a')}</Typography>
                            </TimelineContent>
                        </Stack>
                    </TimelineItem>
                ))}
            </Timeline >
            <Stack direction='column' spacing={2}>
                <RichtextFormatter
                    label={title ?? 'Comment'}
                    placeholder={goalDisabled ? '' : 'Comment'}
                    disabled={goalDisabled}
                    goalDisabled={goalDisabled}
                    initialValue={comment}
                    setTextfieldHelpText={setTextfieldHelpText}
                    setErrorTextfield={setErrorTextfield}
                    setComment={setComment}
                    helperText={textfieldHelpText}
                    error={errorTextfield}
                />
                
                <Button
                    disabled={goalDisabled}
                    variant='outlined'
                    sx={{ width: '20%', alignSelf: 'flex-end', marginTop: 1 }}
                    onClick={handleClickAddComment}
                >
                    Add Comment
                </Button>
            </Stack>
        </>
    );
}
